import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useActualIncidentId } from './commonHooks';
import axios from 'axios';
import { apiPrefix } from 'constants/apiConfig';
import { ResourceStatus } from '../Resources/constants/resourceConstants';
import { CheckoffRecord } from '../Resources/types/resourceTypes';
import { keys as resourceSetKeys } from './resourceSetHooks';

// Query Keys
type Filters = {
  current_status?: string;
  resource_type?: string;
  exclude_checked_out?: boolean;
};

export const resourcesInIncidentKeys = {
  base: ['resources-in-incident'] as const,
  byIncidentId: (incidentId: string) =>
    [...resourcesInIncidentKeys.base, incidentId] as const,
  byIncidentIdWithFilters: (incidentId: string, filters?: Filters) =>
    filters
      ? ([
          ...resourcesInIncidentKeys.byIncidentId(incidentId),
          filters,
        ] as const)
      : resourcesInIncidentKeys.byIncidentId(incidentId),
};

// Types
export interface GeoLocation {
  lat?: number;
  lng?: number;
  name?: string;
  address?: string;
}

export interface ResourceInIncident {
  id: string;
  resource_in_incident_id: string;
  resource_set_id: string;
  incident_id: string;
  group_guid: string;

  // ICS 211 Header Information (Items 1-3)
  operational_period_from?: string;
  operational_period_to?: string;

  // ICS 211 Check-in Data (Item 4)
  checkin_datetime: string;
  checkin_by_user_guid: string;

  // ICS 211 Resource Classification (Item 5)
  state_and_agency?: string;
  resource_category?: string;
  resource_kind?: string;
  resource_type?: string;
  resource_name_identifier?: string;
  strike_team_task_force?: string; // ST/TF/null

  // ICS 211 Resource Details (Items 6-14)
  order_request_number?: string;
  leader_name: string;
  personnel_count: number;
  incident_contact_info?: string;
  incident_lodging_info?: string;
  home_unit_agency?: string;
  departure_point?: string;
  departure_datetime?: string;
  travel_method?: string;
  work_assignment_id?: string;
  incident_assignment_location?: string;
  other_qualifications?: string;

  // Geolocation fields
  location_geolocation?: GeoLocation | null;
  resource_site_id?: string;
  departure_geolocation?: GeoLocation | null;
  departure_site_id?: string;
  assignment_geolocation?: GeoLocation | null;
  assignment_site_id?: string;
  destination_geolocation?: GeoLocation | null;
  destination_site_id?: string;

  // ICS 211 Data Transmission (Items 15-16)
  data_sent_to_resl_datetime?: string;
  data_sent_to_resl_initials?: string;
  prepared_by_name?: string;
  prepared_by_position?: string;
  prepared_by_signature?: string;
  prepared_datetime?: string;

  // ICS 219 T-Card Status Tracking
  current_status: ResourceStatus;
  assignment_details?: string;
  status_since: string;
  estimated_return_time?: string;
  status_notes?: string;
  last_status_update_by?: string;
  last_status_update_datetime?: string;

  // ICS 221/238 Demobilization Data
  checkout_datetime?: string;
  checkout_by_user_guid?: string;
  release_method?: string;
  checkout_destination?: string;
  checkout_eta_home?: string;
  checkout_travel_method?: string;
  checkout_contact_info?: string;
  checkout_remarks?: string;
  checkout_checkoffs?: Record<string, CheckoffRecord>;

  // Additional ICS 221 fields
  planned_release_datetime?: string;
  resource_personnel_released?: string;
  room_overnight?: boolean;
  estimated_departure_datetime?: string;
  actual_release_datetime?: string;
  estimated_arrival_datetime?: string;
  manifest_required?: boolean;
  manifest_number?: string;
  unit_agency_notified?: string;
  unit_agency_notified_bool?: boolean;
  demob_prepared_by_name?: string;
  demob_prepared_by_position?: string;
  demob_prepared_by_signature?: string;
  demob_prepared_datetime?: string;

  // From joined resource_sets table
  resource_name?: string;
  resource_set_description?: string;
  resource_set_kind?: string;
  resource_set_type?: string;
  resource_set_quantity?: number;

  // Metadata
  version: number;
  created_datetime: string;
  updated_datetime: string;
}

// Fetch resources in incident
export function useResourcesInIncident(filters?: Filters) {
  const incidentId = useActualIncidentId();

  return useQuery({
    queryKey: resourcesInIncidentKeys.byIncidentIdWithFilters(
      incidentId,
      filters
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('incident_id', incidentId);
      if (filters?.current_status)
        params.append('current_status', filters.current_status);
      if (filters?.resource_type)
        params.append('resource_type', filters.resource_type);
      if (filters?.exclude_checked_out)
        params.append(
          'exclude_checked_out',
          String(filters.exclude_checked_out)
        );

      const { data } = await axios.get<ResourceInIncident[]>(
        `${apiPrefix}/dice/ics/ics_resource_in_incident?${params}`
      );
      return data;
    },
    enabled: !!incidentId,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
}

// Add resource to incident (initial creation)
export function useCheckInResource() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (data: Partial<ResourceInIncident>) => {
      const { data: result } = await axios.post(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/add-to-incident`,
        data
      );
      return result;
    },
    onSuccess: () => {
      // Invalidate all variations of the query (exact: false is default)
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      queryClient.invalidateQueries({
        queryKey: resourceSetKeys.all,
      });
      queryClient.refetchQueries({
        queryKey: resourceSetKeys.all,
        type: 'active',
      });
      // Also invalidate roster queries to refresh user check-in status
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
    },
  });
}

// Update resource check-in information
export function useUpdateResourceCheckIn() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/checkin`,
        data
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      queryClient.invalidateQueries({
        queryKey: resourceSetKeys.all,
      });
      queryClient.refetchQueries({
        queryKey: resourceSetKeys.all,
        type: 'active',
      });
    },
  });
}

// Update resource status
export function useUpdateResourceStatus() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/status`,
        data
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      queryClient.invalidateQueries({
        queryKey: resourceSetKeys.all,
      });
      queryClient.refetchQueries({
        queryKey: resourceSetKeys.all,
        type: 'active',
      });
      // Also invalidate roster queries to refresh user check-in status
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
    },
  });
}

// Check out resource
export function useCheckOutResource() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ResourceInIncident>;
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/checkout`,
        data
      );
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      queryClient.invalidateQueries({
        queryKey: resourceSetKeys.all,
      });
      queryClient.refetchQueries({
        queryKey: resourceSetKeys.all,
        type: 'active',
      });
      // Also invalidate roster queries to refresh user check-in status
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
    },
  });
}

// Update resource work assignments (reciprocal relationship)
export function useUpdateResourceWorkAssignments() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      workAssignmentIds,
    }: {
      id: string;
      workAssignmentIds: string[];
    }) => {
      const { data: result } = await axios.put(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}/work-assignments`,
        { work_assignment_ids: workAssignmentIds }
      );
      return result;
    },
    onSuccess: () => {
      // Invalidate both resources and work assignments queries
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      // Invalidate all work assignment queries regardless of user_guid
      queryClient.invalidateQueries({
        queryKey: ['incident_workAssignments', incidentId],
      });
    },
  });
}

// Delete resource from incident
export function useDeleteResourceInIncident() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axios.delete(
        `${apiPrefix}/dice/ics/ics_resource_in_incident/${id}`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate all queries that start with 'resources-in-incident' and the incidentId
      queryClient.invalidateQueries({
        queryKey: resourcesInIncidentKeys.byIncidentId(incidentId),
      });
      // Also invalidate resource sets to update the organization view
      queryClient.invalidateQueries({
        queryKey: ['resource_sets'],
      });
    },
  });
}
